import * as React from 'react';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Link } from 'react-router-dom';

import { listerSeances, type Seance } from '../Construction/seancesApi.ts';
import {
  categorieDe,
  composerNotation,
  definirModeleParDefaut,
  modeleApplique,
  partiesModele,
  useModelesParDefaut,
  USAGES_PAR_DEFAUT,
  modeleDefini,
  type UsageParDefaut,
  type InfoUsageParDefaut,
} from '../Construction/composition.ts';
import { estCoach, nomAthlete, useMoi, type AthleteRattache } from '../Auth/moi.ts';
import { convientA, DISTANCES, libelleDistance, nomAvecDistance } from '../Construction/distanceCourse.ts';

const AUCUN = '';

// Choix des modeles de seance par defaut (page Mes preferences) : un pour
// les seances de repetitions, un pour les sorties longues / tempo continu.
// Ils composent chaque corps de seance pose dans le calendrier. Le coach les
// choisit pour lui ou pour un de ses athletes (parametre athlete de
// panneau de l'athlete, page Athletes). Reserve au coach : l'athlete ne
// voit pas ses modeles. Les modeles proposes sont filtres par course :
// celle que prepare l'athlete (page Athletes, Apercu), modifiable ici pour
// parcourir les autres ; les modeles « toutes distances » sont toujours proposes.
export default function ModeleSeanceEditeur({ athlete: athleteChoisi = null }: { athlete?: AthleteRattache | null }) {
  const moi = useMoi();
  const coach = estCoach(moi);
  const athlete: string | null = coach ? athleteChoisi?.ssoUserId ?? null : null;

  const modeles = useModelesParDefaut(0, athlete, coach);
  const [seances, setSeances] = React.useState<Seance[]>([]);
  const [etat, setEtat] = React.useState<'' | 'enregistrement' | 'enregistre' | 'erreur'>('');
  const [erreur, setErreur] = React.useState('');
  // Course dont les modeles sont proposes ('' = toutes) : par defaut, celle
  // que prepare l'athlete.
  const distancePreparee = athleteChoisi?.distancePreparee ?? '';
  const [distance, setDistance] = React.useState<string>(distancePreparee);
  React.useEffect(() => setDistance(distancePreparee), [distancePreparee]);

  React.useEffect(() => {
    if (!coach) return;
    listerSeances()
      .then(setSeances)
      .catch(() => setSeances([]));
  }, [coach]);

  const tousLesModeles = seances.filter((s) => categorieDe(s) === 'MODELE');
  const disponibles = tousLesModeles.filter((s) => convientA(s, distance));
  // Parties des modeles : les seances du coach, ou celles renvoyees avec les
  // modeles (l'athlete ne voit pas les seances de son coach).
  const toutes = [...seances, ...(modeles.parties ?? [])];

  if (!coach) return null;

  const choisir = (usage: UsageParDefaut, id: string) => {
    setEtat('enregistrement');
    definirModeleParDefaut(usage, id || null, athlete)
      .then(() => setEtat('enregistre'))
      .catch((e: Error) => {
        setErreur(e.message);
        setEtat('erreur');
      });
  };

  const composition = (modele: Seance | null) => {
    const parties = partiesModele(modele, toutes);
    return composerNotation(parties.echauffement?.notation, '[corps de séance]', parties.recuperation?.notation);
  };

  return (
    <Box id="preference-modele-seance" sx={{ mt: 4 }}>
      <h2>Modèles de séance</h2>
      <Typography sx={{ mb: 2 }} color="text.secondary">
        Un modèle encadre chaque corps de séance posé dans le calendrier par un échauffement et une récupération. Le
        modèle est choisi selon le type de la séance : « Répétitions » pour le fractionné (court, moyen, long, seuil,
        pyramide, combinée), « Sortie longue / continu » pour le tempo continu et l'endurance.
        {coach && <> Tu peux forcer un autre choix sur chaque séance (page <Link to="/seances">Séances</Link>).</>} Si
        un seul modèle est défini, il sert pour les deux. Dans une semaine d'assimilation ou d'affûtage, les
        répétitions prennent le modèle « Assimilation / affûtage », souvent plus long en récupération. Les séances
        déjà posées gardent leur composition.
      </Typography>

      {coach && (
        <Typography sx={{ mb: 2 }}>
          {athleteChoisi
            ? <>Modèles appliqués aux séances que tu poses dans le calendrier de <b>{nomAthlete(athleteChoisi)}</b>. « Par défaut » : tes propres modèles (page Préférences).</>
            : 'Tes modèles, appliqués à ton calendrier et à ceux de tes athlètes pour lesquels tu n\'en as pas choisi (page Athlètes).'}
        </Typography>
      )}

      <TextField
        select
        label="Course"
        value={distance}
        onChange={(e) => setDistance(e.target.value)}
        SelectProps={{ displayEmpty: true }}
        InputLabelProps={{ shrink: true }}
        helperText={athleteChoisi
          ? `Course préparée par ${nomAthlete(athleteChoisi)} : ${libelleDistance(distancePreparee) ?? 'non renseignée (onglet Aperçu)'}. Seuls les modèles de la course choisie, et ceux « toutes distances », sont proposés.`
          : 'Seuls les modèles de la course choisie, et ceux « toutes distances », sont proposés.'}
        sx={{ mb: 3, maxWidth: 520 }}
        fullWidth
      >
        <MenuItem value={AUCUN}>Toutes les courses</MenuItem>
        {DISTANCES.map((d) => <MenuItem key={d.code} value={d.code}>{d.libelle}</MenuItem>)}
      </TextField>

      {/* Un modele par ligne : la liste, puis sa composition. */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, maxWidth: 520 }}>
        {USAGES_PAR_DEFAUT.map((u: InfoUsageParDefaut) => {
          const modele = modeleDefini(modeles, u.code);
          // Modele du coach applique a defaut de choix pour l'athlete.
          const parDefaut = modeles.parDefaut ? modeleApplique(modeles.parDefaut, u.code) : null;
          const libelleAucun = athleteChoisi
            ? `Par défaut${parDefaut ? ` (mon modèle : ${parDefaut.nom})` : ' (aucun)'}`
            : u.code === 'ASSIMILATION' ? 'Aucun (modèle répétitions)' : 'Aucun';
          const affiche = modele ?? parDefaut;
          // Modele choisi d'une autre course : garde dans la liste (signale).
          const options = modele && !disponibles.some((m) => m.id === modele.id) ? [...disponibles, modele] : disponibles;
          return (
            <Box key={u.code}>
              <TextField
                select
                fullWidth
                label={`Modèle ${u.libelle.toLowerCase()}`}
                value={modele?.id ?? AUCUN}
                onChange={(e) => choisir(u.code, e.target.value)}
                SelectProps={{ displayEmpty: true }}
                InputLabelProps={{ shrink: true }}
                helperText={u.description}
              >
                <MenuItem value={AUCUN}>{libelleAucun}</MenuItem>
                {options.map((m: Seance) => (
                  <MenuItem key={m.id} value={m.id}>
                    {nomAvecDistance(m)}{convientA(m, distance) ? '' : ' — autre course'}
                  </MenuItem>
                ))}
              </TextField>
              {affiche && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  <code>{composition(affiche)}</code>
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
      {coach && tousLesModeles.length > 0 && disponibles.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Aucun modèle pour cette course : indique la course d'un modèle sur la page <Link to="/seances">Séances</Link>.
        </Typography>
      )}
      {coach && tousLesModeles.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Aucun modèle enregistré pour l'instant : crée-en sur la page Séances (catégorie « Modèle »).
        </Typography>
      )}
      <Typography variant="caption" color={etat === 'erreur' ? 'error' : 'text.secondary'} component="div">
        {etat === 'enregistrement' && 'Enregistrement…'}
        {etat === 'enregistre' && 'Enregistré'}
        {etat === 'erreur' && `Enregistrement impossible (${erreur})`}
      </Typography>
    </Box>
  );
}
